import { useEffect, useRef, useState } from 'react';
import type { TemplateField } from '../api/types';
import { captureAndUpload } from '../lib/media';

/** Photo/video capture for a `photo`/`video` template field — captures via the
 *  phone's camera app, uploads immediately (requires connectivity), and reports
 *  the resulting Drive URL back as the field's value. */
export default function MediaField({
  field, value, equipmentId, onChange,
}: {
  field: TemplateField;
  value: string;
  equipmentId: string;
  onChange: (url: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const kind: 'photo' | 'video' = field.type === 'video' ? 'video' : 'photo';

  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow retaking the same shot
    if (!file) return;
    if (kind === 'photo') {
      const url = URL.createObjectURL(file);
      setPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return url; });
    }
    setBusy(true); setErr('');
    try {
      const uploaded = await captureAndUpload(file, kind, equipmentId, field.key);
      onChange(uploaded);
    } catch (ex) {
      setErr(ex instanceof Error ? ex.message : 'Upload failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="media-field">
      <input
        ref={inputRef} type="file" accept={kind === 'photo' ? 'image/*' : 'video/*'} capture="environment"
        style={{ display: 'none' }} onChange={(e) => void onFile(e)}
      />
      {kind === 'photo' && previewUrl && <img src={previewUrl} alt="" className="media-thumb" />}
      <div className="media-row">
        <button type="button" className="secondary" disabled={busy} onClick={() => inputRef.current?.click()}>
          {busy ? 'Uploading…' : value ? `Retake ${kind}` : kind === 'photo' ? '📷 Take photo' : '🎥 Record video'}
        </button>
        {value && !busy && <span className="media-ok">✓ Uploaded <a href={value} target="_blank" rel="noreferrer">view</a></span>}
      </div>
      {err && <div className="err">{err}</div>}
    </div>
  );
}
